import React, { useState, useEffect, useRef } from 'react';

// X Logo SVG Component for consistent branding
const XLogo = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-label="X logo">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ProfileMenu = ({ user, isDarkMode, setIsDarkMode, onLogout, onNavigateToAccount, onRestartTour }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Check if user is connected via X/Twitter
  const isXUser = user?.x_username || user?.username;
  const xUsername = user?.x_username || user?.username;
  const xProfileImage = user?.x_profile_image_url || user?.profile_image_url;
  const xDisplayName = user?.x_name || user?.name;

  return (
    <div className="relative" ref={menuRef}>
      {/* Profile Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="profile-menu-btn"
        className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white text-xs font-bold hover:from-orange-500 hover:to-orange-700 transition-all duration-200 ring-2 ring-white/20 hover:ring-white/40"
        title={xDisplayName || xUsername || 'Profile'}
      >
        {xProfileImage ? (
          <img 
            src={xProfileImage} 
            alt="Profile" 
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          getInitials()
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute right-0 mt-2 w-64 rounded-xl shadow-xl border z-50 overflow-hidden ${
          isDarkMode 
            ? 'bg-[#1a2235] border-slate-700' 
            : 'bg-white border-gray-200'
        }`}>
          {/* X Identity Header - Per X Brand Guidelines */}
          <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
            {isXUser ? (
              <div className="flex items-start gap-3">
                {/* X Profile Image */}
                {xProfileImage && (
                  <img 
                    src={xProfileImage} 
                    alt={`@${xUsername}`}
                    className="w-10 h-10 rounded-full flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  {/* X Logo + Display Name */}
                  <div className="flex items-center gap-1.5">
                    <XLogo className="w-3.5 h-3.5 flex-shrink-0" />
                    <p className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {xDisplayName || xUsername}
                    </p>
                  </div>
                  {/* @handle */}
                  <p className={`text-sm truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    @{xUsername}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {user?.name || user?.username || 'User'}
                </p>
                {user?.email && (
                  <p className={`text-sm truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {user.email}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* Account */}
            <button
              onClick={() => {
                onNavigateToAccount?.();
                setIsOpen(false);
              }}
              data-testid="account-menu-item"
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                isDarkMode 
                  ? 'text-slate-200 hover:bg-slate-700' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Account
            </button>

            {/* Divider */}
            <div className={`my-1 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`} />

            {/* Dark/Light Mode Toggle */}
            <div className={`px-4 py-2.5 flex items-center justify-between ${
              isDarkMode ? 'text-slate-200' : 'text-gray-700'
            }`}>
              <div className="flex items-center gap-3">
                {isDarkMode ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/>
                  </svg>
                )}
                <span className="text-sm">{isDarkMode ? 'Dark Mode' : 'Light Mode'}</span>
              </div>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  isDarkMode ? 'bg-orange-500' : 'bg-gray-300'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  isDarkMode ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>

            {/* Divider */}
            <div className={`my-1 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`} />

            {/* Restart Tour */}
            <button
              onClick={() => {
                onRestartTour?.();
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                isDarkMode 
                  ? 'text-slate-200 hover:bg-slate-700' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Restart Tour
            </button>

            {/* Divider */}
            <div className={`my-1 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`} />

            {/* Sign Out */}
            <button
              onClick={() => {
                onLogout?.();
                setIsOpen(false);
              }}
              data-testid="logout-btn"
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                isDarkMode 
                  ? 'text-red-400 hover:bg-slate-700' 
                  : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;
